import { AlertTypes, GenericFormContainer, Leap4Breadcrumbs, Mode, setAlert, Spinner, MuiIcons, Grid } from "@leap4/frontend";
import { useMemo, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { getObjectById } from "../../../../actions/getObjectData";
import { demandBaseUrl } from "../../../../config/generals";
import { urlService } from "../../../routing/urlsService";
import { createNewObject, updateObjectById } from "../../../../actions/creatingObjectsFunc";
import { DemandContext } from "../base/demandContext";
import { DetailsForm } from "../base/detailsForm";
import { initialDemandObj } from "../demands/demand.interface";
import { validateDemandForm } from "../../../utils/form/demandFormValidator";
import { ActivePublishers } from "../base/activePublishers";

const { Create: CreateIcon } = MuiIcons;

const mapStateToProps = () => ({});

const mapActions = { setAlert };

const connector = connect(mapStateToProps, mapActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

const _manageDemand = (props: PropsFromRedux) => {
    const params = useParams<{ id?: string }>();
    const history = useHistory();
    const [demand, setDemand] = useState(initialDemandObj);
    const demandContextValue = useMemo(() => ({ demand, setDemand }), [demand, setDemand]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadError, setIsLoadError] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const fetchDemandById = async () => {
        if (!params.id) {
            return;
        }
        const demandData = await getObjectById(demandBaseUrl, params.id);

        if(demandData.globalBundleBlackList === undefined){
            demandData.globalBundleBlackList = [];
        }
        if(demandData.globalBundleWhiteList === undefined){
            demandData.globalBundleWhiteList = [];
        }
        setDemand(demandData);
    };

    useQuery("fetchDemandById", fetchDemandById, {
        refetchOnWindowFocus: false,
        onError: () => {
            setIsLoadError(true);
        },
    });

    const onSubmit = async () => {
        const { valid } = validateDemandForm(demand);
        if (!valid) {
            setSubmitted(true);
            return;
        }
        try {
            setIsLoading(true);
            if (params.id) {
                await updateObjectById(params.id, demand, demandBaseUrl);
            } else {
                await createNewObject(demand, demandBaseUrl);
            }
            props.setAlert({ msg: "Demand saved successfully!", type: AlertTypes.SUCCESS });
            history.push(urlService.demands());
        } catch (err) {
            props.setAlert({ msg: err.toString(), type: AlertTypes.FAILURE });
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoadError) return <>Error loading page</>;
    if (isLoading) return <Spinner />;

    return (
        <>
            <Leap4Breadcrumbs
                breadcrumbs={[
                    { text: "Demand Management", link: urlService.demands(), icon: <CreateIcon /> },
                    { text: "Demand View", icon: <CreateIcon /> },
                ]}
            />
            <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={params.id ? 9 : 12}>
                    <GenericFormContainer
                        mode={params.id ? Mode.Edit : Mode.Create}
                        onCreateOrEditFunc={onSubmit}
                        onDeleteFunction={async () => {
                            await updateObjectById(params.id, { isDeleted: true }, demandBaseUrl);
                            history.push(urlService.demands());
                        }}
                    >
                        <DemandContext.Provider value={demandContextValue}>
                            <DetailsForm submitted={submitted} mode={params.id ? "edit" : "create"} />
                        </DemandContext.Provider>
                    </GenericFormContainer>
                </Grid>
                {params.id && (
                    <Grid item xs={3} sx={{ position: "sticky", top: 16 }}>
                        <ActivePublishers demandId={params.id} />
                    </Grid>
                )}
            </Grid>
        </>
    );
};

export const ManageDemand = connector(_manageDemand);
